import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Mark } from '@tiptap/core';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import mammoth from 'mammoth';
import { MDBBtn, MDBIcon, MDBInput, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody, MDBModalFooter, MDBModalTitle } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import './TiptapEditor.scss';

// Custom Indent extension — adjusts margin-left on block elements
import { Extension } from '@tiptap/core';

const INDENT_STEP = 40; // px per indent level

const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading', 'bulletList', 'orderedList'],
      attributes: {
        indent: {
          default: 0,
          parseHTML: (el) => parseInt(el.style.marginLeft) || 0,
          renderHTML: (attrs) => attrs.indent ? { style: `margin-left: ${attrs.indent}px` } : {},
        },
        blockBg: {
          default: null,
          parseHTML: (el) => el.style.backgroundColor || null,
          renderHTML: (attrs) => {
            if (!attrs.blockBg) return {};
            return { style: `background-color: ${attrs.blockBg}; padding: 4px 8px;` };
          },
        },
      },
    }];
  },

  addCommands() {
    return {
      increaseIndent: () => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.isBlock && node.attrs.indent !== undefined) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: (node.attrs.indent || 0) + INDENT_STEP });
          }
        });
        if (dispatch) dispatch(tr);
        return true;
      },
      setBlockBg: (color) => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.isBlock && node.attrs.blockBg !== undefined) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockBg: color });
          }
        });
        if (dispatch) dispatch(tr);
        return true;
      },
      unsetBlockBg: () => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.isBlock && node.attrs.blockBg !== undefined) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockBg: null });
          }
        });
        if (dispatch) dispatch(tr);
        return true;
      },
      decreaseIndent: () => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (node.isBlock && node.attrs.indent !== undefined) {
            const newIndent = Math.max(0, (node.attrs.indent || 0) - INDENT_STEP);
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: newIndent });
          }
        });
        if (dispatch) dispatch(tr);
        return true;
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        // In lists: let Tiptap handle sinkListItem (nesting)
        if (editor.isActive('orderedList') || editor.isActive('bulletList') || editor.isActive('taskList')) {
          return false;
        }
        // Otherwise: insert inline em-spaces
        editor.chain().focus().insertContent('\u2003\u2003\u2003').run();
        return true;
      },
      'Shift-Tab': ({ editor }) => {
        // In lists: let Tiptap handle liftListItem
        if (editor.isActive('orderedList') || editor.isActive('bulletList') || editor.isActive('taskList')) {
          return false;
        }
        editor.chain().focus().decreaseIndent().run();
        return true;
      },
    };
  },
});

// Custom FontSize extension — renders as <span style="font-size: Xpx">
const FontSize = Mark.create({
  name: 'fontSize',

  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (element) => element.style.fontSize?.replace('px', '') || null,
        renderHTML: (attributes) => {
          if (!attributes.size) return {};
          return { style: `font-size: ${attributes.size}px` };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span', getAttrs: (el) => el.style.fontSize ? {} : false }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark(this.name, { size }).run(),
      unsetFontSize: () => ({ chain }) => chain().unsetMark(this.name).run(),
    };
  },
});

const FONT_SIZES = [12, 14, 16, 18, 24, 30, 36, 48, 60, 72];

const MenuBar = ({ editor, onImportDocx, onOpenLinkModal, onOpenHtmlModal, onToggleTableBorders }) => {
  const { t } = useTranslation();

  if (!editor) return null;

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const getActiveBlockType = () => {
    for (let level = 1; level <= 6; level++) {
      if (editor.isActive('heading', { level })) return `h${level}`;
    }
    return 'paragraph';
  };

  const getActiveFontSize = () => {
    const attrs = editor.getAttributes('fontSize');
    return attrs?.size || '';
  };

  const handleBlockTypeChange = (e) => {
    const value = e.target.value;
    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      const level = parseInt(value.replace('h', ''), 10);
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  const handleFontSizeChange = (e) => {
    const size = e.target.value;
    if (size) {
      editor.chain().focus().setFontSize(size).run();
    } else {
      editor.chain().focus().unsetFontSize().run();
    }
  };

  return (
    <div className="tiptap-toolbar">
      <div className="tiptap-toolbar-group">
        <select
          className="tiptap-select"
          value={getActiveBlockType()}
          onChange={handleBlockTypeChange}
          title={t('Block Type')}
        >
          <option value="paragraph">Normal</option>
          <option value="h1">H1</option>
          <option value="h2">H2</option>
          <option value="h3">H3</option>
          <option value="h4">H4</option>
          <option value="h5">H5</option>
          <option value="h6">H6</option>
        </select>
        <select
          className="tiptap-select tiptap-select-sm"
          value={getActiveFontSize()}
          onChange={handleFontSizeChange}
          title={t('Font Size')}
        >
          <option value="">{t('Size')}</option>
          {FONT_SIZES.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>

      <div className="tiptap-toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title={t('Bold')}
        >
          <MDBIcon fas icon="bold" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title={t('Italic')}
        >
          <MDBIcon fas icon="italic" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title={t('Underline')}
        >
          <MDBIcon fas icon="underline" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title={t('Strikethrough')}
        >
          <MDBIcon fas icon="strikethrough" />
        </button>
      </div>

      <div className="tiptap-toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title={t('Bullet List')}
        >
          <MDBIcon fas icon="list-ul" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title={t('Ordered List')}
        >
          <MDBIcon fas icon="list-ol" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className={editor.isActive('taskList') ? 'is-active' : ''}
          title={t('Checkbox List')}
        >
          <MDBIcon fas icon="tasks" />
        </button>
        <button
          type="button"
          onClick={() => {
            if (editor.isActive('bulletList')) {
              // If already a bullet list, toggle the unstyled class
              const { from } = editor.state.selection;
              editor.state.doc.nodesBetween(from, from, (node, pos) => {
                if (node.type.name === 'bulletList') {
                  const currentClass = node.attrs.HTMLAttributes?.class || '';
                  const isUnstyled = currentClass.includes('unstyled-list');
                  editor.view.dispatch(
                    editor.state.tr.setNodeMarkup(pos, undefined, {
                      ...node.attrs,
                      HTMLAttributes: { class: isUnstyled ? '' : 'unstyled-list' },
                    })
                  );
                }
              });
            } else {
              editor.chain().focus().toggleBulletList().run();
            }
          }}
          title={t('List without markers')}
        >
          <MDBIcon fas icon="minus" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().increaseIndent().run()}
          title={t('Increase Indent')}
        >
          <MDBIcon fas icon="indent" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().decreaseIndent().run()}
          title={t('Decrease Indent')}
        >
          <MDBIcon fas icon="outdent" />
        </button>
      </div>

      <div className="tiptap-toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          title={t('Align Left')}
        >
          <MDBIcon fas icon="align-left" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          title={t('Align Center')}
        >
          <MDBIcon fas icon="align-center" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          title={t('Align Right')}
        >
          <MDBIcon fas icon="align-right" />
        </button>
      </div>

      <div className="tiptap-toolbar-group">
        <label className="tiptap-color-picker" title={t('Text Color')}>
          <MDBIcon fas icon="font" />
          <input
            type="color"
            value={editor.getAttributes('textStyle')?.color || '#000000'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        <label className="tiptap-color-picker" title={t('Background Color')}>
          <MDBIcon fas icon="highlighter" />
          <input
            type="color"
            value={editor.getAttributes('highlight')?.color || '#FFFF00'}
            onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
          />
        </label>
        <label className="tiptap-color-picker" title={t('Line Background')}>
          <MDBIcon fas icon="paint-roller" />
          <input
            type="color"
            value="#A6A6A6"
            onChange={(e) => editor.chain().focus().setBlockBg(e.target.value).run()}
          />
        </label>
        {(editor.isActive('textStyle') || editor.isActive('highlight')) && (
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetColor().unsetHighlight().unsetBlockBg().run()}
            title={t('Remove Colors')}
          >
            <MDBIcon fas icon="eraser" />
          </button>
        )}
      </div>

      <div className="tiptap-toolbar-group">
        <button
          type="button"
          onClick={onOpenLinkModal}
          className={editor.isActive('link') ? 'is-active' : ''}
          title={t('Link')}
        >
          <MDBIcon fas icon="link" />
        </button>
        {editor.isActive('link') && (
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetLink().run()}
            title={t('Remove Link')}
          >
            <MDBIcon fas icon="unlink" />
          </button>
        )}
      </div>

      <div className="tiptap-toolbar-group">
        <button
          type="button"
          onClick={addTable}
          title={t('Insert Table')}
        >
          <MDBIcon fas icon="table" />
        </button>
        {editor.isActive('table') && (
          <>
            <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()} title={t('Add Column')}>
              <MDBIcon fas icon="columns" />
            </button>
            <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()} title={t('Add Row')}>
              <MDBIcon fas icon="plus" />
            </button>
            <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()} title={t('Delete Column')}>
              <MDBIcon fas icon="minus" />
            </button>
            <button type="button" onClick={() => editor.chain().focus().deleteRow().run()} title={t('Delete Row')}>
              <MDBIcon fas icon="minus-circle" />
            </button>
            <label className="tiptap-color-picker" title={t('Cell Background')}>
              <MDBIcon fas icon="fill-drip" />
              <input
                type="color"
                value="#f0f0f0"
                onChange={(e) => editor.chain().focus().setCellAttribute('backgroundColor', e.target.value).run()}
              />
            </label>
            <button
              type="button"
              onClick={() => editor.chain().focus().setCellAttribute('backgroundColor', null).run()}
              title={t('Remove Cell Background')}
            >
              <MDBIcon fas icon="eraser" />
            </button>
            <button type="button" onClick={onToggleTableBorders} title={t('Toggle Borders')}>
              <MDBIcon fas icon="border-all" />
            </button>
            <button type="button" onClick={() => editor.chain().focus().deleteTable().run()} title={t('Delete Table')}>
              <MDBIcon fas icon="trash" />
            </button>
          </>
        )}
      </div>

      <div className="tiptap-toolbar-group">
        <button type="button" onClick={() => editor.chain().focus().undo().run()} title={t('Undo')}>
          <MDBIcon fas icon="undo" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} title={t('Redo')}>
          <MDBIcon fas icon="redo" />
        </button>
      </div>

      <div className="tiptap-toolbar-group">
        <button type="button" onClick={onImportDocx} title={t('Import Word Document')}>
          <MDBIcon fas icon="file-word" />
        </button>
        <button type="button" onClick={onOpenHtmlModal} title={t('Insert HTML')}>
          <MDBIcon fas icon="code" />
        </button>
      </div>
    </div>
  );
};

const TiptapEditor = ({ content, onChange, onFullscreen }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Link modal state
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Confirm import modal state
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  // HTML modal state
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlInput, setHtmlInput] = useState('');

  // Table borders toggle
  const [tableBordersHidden, setTableBordersHidden] = useState(false);
  const handleToggleTableBorders = useCallback(() => {
    setTableBordersHidden(prev => !prev);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      FontSize,
      Indent,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            backgroundColor: {
              default: null,
              parseHTML: (el) => el.style.backgroundColor || null,
              renderHTML: (attrs) => attrs.backgroundColor ? { style: `background-color: ${attrs.backgroundColor}` } : {},
            },
          };
        },
      }),
      TableHeader.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            backgroundColor: {
              default: null,
              parseHTML: (el) => el.style.backgroundColor || null,
              renderHTML: (attrs) => attrs.backgroundColor ? { style: `background-color: ${attrs.backgroundColor}` } : {},
            },
          };
        },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Link.configure({ openOnClick: false }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // HTML modal handlers
  const handleOpenHtmlModal = useCallback(() => {
    setHtmlInput(editor?.getHTML() || '');
    setHtmlModalOpen(true);
  }, [editor]);

  const handleConfirmHtml = useCallback(() => {
    if (editor && htmlInput) {
      editor.commands.setContent(htmlInput);
    }
    setHtmlModalOpen(false);
    setHtmlInput('');
  }, [editor, htmlInput]);

  const handleCancelHtml = useCallback(() => {
    setHtmlModalOpen(false);
    setHtmlInput('');
  }, []);

  // Sync external content changes
  const isInternalUpdate = useRef(false);
  useEffect(() => {
    if (editor && content !== undefined && !editor.isFocused) {
      const currentHTML = editor.getHTML();
      if (content !== currentHTML && !isInternalUpdate.current) {
        editor.commands.setContent(content || '', false);
      }
    }
    isInternalUpdate.current = false;
  }, [content, editor]);

  // Link modal handlers
  const handleOpenLinkModal = useCallback(() => {
    const existingUrl = editor?.getAttributes('link')?.href || '';
    setLinkUrl(existingUrl);
    setLinkModalOpen(true);
  }, [editor]);

  const handleConfirmLink = useCallback(() => {
    if (linkUrl && editor) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setLinkModalOpen(false);
    setLinkUrl('');
  }, [linkUrl, editor]);

  const handleCancelLink = useCallback(() => {
    setLinkModalOpen(false);
    setLinkUrl('');
  }, []);

  // Word import handlers
  const handleImportDocx = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const importFile = useCallback(async (file) => {
    if (!file || !editor) return;
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      let html = result.value;

      // Convert tab-separated paragraphs into table rows.
      // Detects consecutive <p> elements containing 2+ tabs and groups them into <table>.
      html = html.replace(
        /(<p>([^<]*\t[^<]*\t[^<]*)<\/p>\s*)+/g,
        (block) => {
          const rows = block.match(/<p>([^<]*)<\/p>/g);
          if (!rows || rows.length < 2) return block;

          const tableRows = rows.map((row, i) => {
            const text = row.replace(/<\/?p>/g, '');
            const cells = text.split(/\t+/).map(c => c.trim()).filter(Boolean);
            if (cells.length < 2) return null;
            const tag = i === 0 ? 'th' : 'td';
            return `<tr>${cells.map(c => `<${tag}>${c}</${tag}>`).join('')}</tr>`;
          }).filter(Boolean);

          if (tableRows.length < 2) return block;
          return `<table>${tableRows.join('')}</table>`;
        }
      );

      editor.commands.setContent(html);
    } catch (err) {
      console.error('Error importing .docx:', err);
    }
  }, [editor]);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;

    const hasContent = editor.getHTML() !== '<p></p>' && editor.getHTML() !== '';
    if (hasContent) {
      setPendingFile(file);
      setConfirmImportOpen(true);
    } else {
      await importFile(file);
    }

    e.target.value = '';
  }, [editor, importFile]);

  const handleConfirmImport = useCallback(async () => {
    if (pendingFile) {
      await importFile(pendingFile);
    }
    setPendingFile(null);
    setConfirmImportOpen(false);
  }, [pendingFile, importFile]);

  const handleCancelImport = useCallback(() => {
    setPendingFile(null);
    setConfirmImportOpen(false);
  }, []);

  return (
    <div
      className={`tiptap-editor-wrapper ${isFocused ? 'is-focused' : ''} ${tableBordersHidden ? 'hide-table-borders' : ''}`}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsFocused(false);
        }
      }}
    >
      <MenuBar editor={editor} onImportDocx={handleImportDocx} onOpenLinkModal={handleOpenLinkModal} onOpenHtmlModal={handleOpenHtmlModal} onToggleTableBorders={handleToggleTableBorders} />
      <EditorContent editor={editor} className="tiptap-editor-content" />
      <input
        ref={fileInputRef}
        type="file"
        accept=".doc,.docx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {onFullscreen && (
        <button
          type="button"
          className="tiptap-fullscreen-btn"
          onClick={() => onFullscreen(editor)}
          title={t('Fullscreen')}
        >
          <MDBIcon fas icon="expand" />
        </button>
      )}

      {/* Link URL Modal */}
      <MDBModal open={linkModalOpen} onClose={handleCancelLink} tabIndex="-1">
        <MDBModalDialog centered size="sm">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t('Insert Link')}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleCancelLink} type="button" />
            </MDBModalHeader>
            <MDBModalBody>
              <MDBInput
                label="URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirmLink()}
                autoFocus
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={handleCancelLink} type="button">{t('Cancel')}</MDBBtn>
              <MDBBtn onClick={handleConfirmLink} type="button">{t('Insert')}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Confirm Import Modal */}
      <MDBModal open={confirmImportOpen} onClose={handleCancelImport} tabIndex="-1">
        <MDBModalDialog centered size="sm">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t('Import Word Document')}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleCancelImport} type="button" />
            </MDBModalHeader>
            <MDBModalBody>
              {t('This will replace the current content. Continue?')}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={handleCancelImport} type="button">{t('Cancel')}</MDBBtn>
              <MDBBtn color="danger" onClick={handleConfirmImport} type="button">{t('Replace')}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* HTML Insert/Edit Modal */}
      <MDBModal open={htmlModalOpen} onClose={handleCancelHtml} tabIndex="-1">
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t('Insert HTML')}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={handleCancelHtml} type="button" />
            </MDBModalHeader>
            <MDBModalBody>
              <textarea
                className="form-control"
                rows={15}
                value={htmlInput}
                onChange={(e) => setHtmlInput(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={handleCancelHtml} type="button">{t('Cancel')}</MDBBtn>
              <MDBBtn onClick={handleConfirmHtml} type="button">{t('Apply')}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
};

export default TiptapEditor;
